import { CommonModule } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatStepperModule } from '@angular/material/stepper';

import { CreatePriceRequest, LocalizedString, PartnerType, ProductType } from '@hjc/masterdata-types';
import { TranslateModule } from '@ngx-translate/core';
import { AddPriceComponent, AddPriceResult } from '@pages/pricing/components/add-price/add-price.component';
import { IntegrationConfig } from '@services/integration-manager.service';
import { PartnerItem } from '@services/partners.service';
import { ProductsService } from '@services/products.service';
import { BasePartnerCreationComponent } from '../base-item-creation.component';
import { IntegrationComponent } from '../components/integration/integration.component';
import { PriceListComponent } from '../components/price-list/price-list.component';
import { CreatePageHeaderComponent } from '../create-page-header/create-page-header.component';
import { HAULIER_INTEGRATION_CONFIGS } from '../partner-integration.configs';
import { environment } from '@environments/environment';

@Component({
  selector: 'md-create-haulier',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatStepperModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    CreatePageHeaderComponent,
    IntegrationComponent,
    PriceListComponent,
    TranslateModule,
    AddPriceComponent
  ],
  templateUrl: './create-haulier.component.html',
  styleUrl: './create-haulier.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class CreateHaulierComponent extends BasePartnerCreationComponent<PartnerItem> implements OnInit, OnDestroy {
  private readonly TOTAL_STEPS = 3;
  private readonly productsService = inject(ProductsService);
  hubSpotFormId = 'a9eebec3-e80d-4b78-9e98-64e4db7020e8';
  hubSpotPortalId = environment.hubspot.portalId;

  override ngOnInit(): void {
    super.ngOnInit();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  protected override getPartnerIntegrationConfigs(): IntegrationConfig[] {
    return HAULIER_INTEGRATION_CONFIGS;
  }

  protected override getTotalSteps(): number {
    return this.TOTAL_STEPS;
  }

  protected override getDetailsRoute(partnerId: string): string[] {
    return ['/app/view/partners/logistics', partnerId];
  }

  protected override async fetchDataForPricing(): Promise<void> {
    this.isLoading.set(true);
    try {
      const productsResponse = await this.productsService.getByProducts();
      const serviceProductsResponse = await this.productsService.getServices();
      const allProducts = [...(productsResponse.data || []), ...(serviceProductsResponse.data || [])];

      let availableProducts = allProducts;
      if (this.primaryItemData?.id) {
        try {
          const existingPrices = await this.pricingService.getPricingForHaulier(this.primaryItemData.id);
          const existingProductIds = new Set(existingPrices.data?.map(p => p.productId) || []);
          availableProducts = allProducts.filter(product => !existingProductIds.has(product.id));
        } catch (error) {
          console.warn('Could not fetch existing prices for filtering:', error);
        }
      }

      this.priceDataForChild.set({
        items: availableProducts,
        measure: this.pricingService.measures
      });
      this.notificationService.info(this.translateService.instant('NOTIFICATIONS.SUCCESS.PRICING_DATA_PREPARED'));
    } finally {
      this.isLoading.set(false);
    }
  }

  protected override createPriceObject(input: AddPriceResult): CreatePriceRequest | undefined {
    if (!this.primaryItemData?.id) {
      this.errorService.showError(this.translateService.instant('NOTIFICATIONS.ERROR.PRIMARY_PARTNER_DATA_MISSING'));
      return undefined;
    }
    return {
      name: input.itemName,
      productType: 'haulier' as ProductType,
      productId: input.itemId,
      partnerType: 'haulier' as PartnerType,
      partnerId: this.primaryItemData.id,
      rates: [{ rate: input.price, validFrom: input.validFrom.toISOString() }],
      unit: input.unit,
      productName: input.itemName,
      partnerName: this.getItemNameLocalized(this.primaryItemData?.['name'] as string | LocalizedString | undefined)
    };
  }

  localizedHaulierNameForDisplay(): string {
    if (!this.primaryItemData?.['name']) return '';
    const name = this.primaryItemData['name'] as { fi?: string; en?: string; sv?: string } | string;
    if (typeof name === 'string') return name;
    const lang = this.currentLang() as 'fi' | 'en' | 'sv';
    return name[lang] || '';
  }

  onDeletePrice(price: AddPriceResult): void {
    this.priceListSignal.update(prices => prices.filter(p => p.itemId !== price.itemId || p.validFrom.getTime() !== price.validFrom.getTime()));
    this.notificationService.info(this.translateService.instant('NOTIFICATIONS.SUCCESS.PRICE_REMOVED'));
  }

  override onPriceAdded(result: AddPriceResult): void {
    const cloned: AddPriceResult = {
      ...result,
      itemName: result.itemName ? { ...result.itemName } : { fi: '', en: '', sv: '' },
      validFrom: result.validFrom ? new Date(result.validFrom.getTime()) : new Date()
    };

    this.priceListSignal.update(prices => [...prices, cloned]);
    this.notificationService.info(this.translateService.instant('NOTIFICATIONS.SUCCESS.PRICING_DATA_PREPARED'));
  }

  async onDone(): Promise<void> {
    try {
      this.isLoading.set(true);
      await this.onSaveDone();
      this.notificationService.success(this.translateService.instant('NOTIFICATIONS.SUCCESS.CREATION_COMPLETE'));
    } finally {
      this.isLoading.set(false);
    }
  }
}
